/**
 * Narrator Reaction Banks
 *
 * Curated reaction strings organized by trigger, tier, and context.
 * These are client-side strings (NOT LLM calls) for real-time cross-fade.
 *
 * Voice tiers:
 * - l1: Playful, witty, TV-show framing (free/anonymous)
 * - l2: Balanced, approachable expert (drama_plus/family_member)
 * - l3: Professional, analytical (dramax)
 */

import type { Reaction, VoiceTier, SeverityBucket, CrossingTarget } from './types';

// ---------------------------------------------------------------------------
// Accept reactions — triggered when user accepts a rewrite suggestion
// ---------------------------------------------------------------------------

type AcceptBank = Record<VoiceTier, Record<SeverityBucket, Reaction[]>>;

export const ACCEPT_REACTIONS: AcceptBank = {
  l1: {
    critical: [
      { key: 'a-l1-cr-1', fallback: 'Wow, defusing the big one first!', energy: 'excited' },
      { key: 'a-l1-cr-2', fallback: 'That was the drama boss fight.', energy: 'peak' },
      { key: 'a-l1-cr-3', fallback: 'Main character energy: choosing peace.', energy: 'engaged' },
    ],
    high: [
      { key: 'a-l1-hi-1', fallback: 'Solid move. The drama levels are dropping.', energy: 'engaged' },
      { key: 'a-l1-hi-2', fallback: 'Plot twist: you chose the high road!', energy: 'excited' },
      { key: 'a-l1-hi-3', fallback: 'Drama meter going down!', energy: 'engaged' },
    ],
    medium: [
      { key: 'a-l1-md-1', fallback: 'Nice catch on that one.', energy: 'calm' },
      { key: 'a-l1-md-2', fallback: 'Smoothing out the rough edges.', energy: 'calm' },
      { key: 'a-l1-md-3', fallback: 'One less spicy moment in this chat.', energy: 'engaged' },
    ],
    low: [
      { key: 'a-l1-lo-1', fallback: 'Even the small tweaks add up.', energy: 'calm' },
      { key: 'a-l1-lo-2', fallback: 'Fine-tuning the vibe.', energy: 'calm' },
      { key: 'a-l1-lo-3', fallback: 'Polishing the details. Respect.', energy: 'calm' },
    ],
  },
  l2: {
    critical: [
      { key: 'a-l2-cr-1', fallback: 'Smart move addressing that pattern first.', energy: 'excited' },
      { key: 'a-l2-cr-2', fallback: 'That was the most impactful change here.', energy: 'engaged' },
      { key: 'a-l2-cr-3', fallback: 'Major tension point resolved.', energy: 'excited' },
    ],
    high: [
      { key: 'a-l2-hi-1', fallback: 'Good call. That adjustment shifts the whole tone.', energy: 'engaged' },
      { key: 'a-l2-hi-2', fallback: 'Noticeable improvement in clarity there.', energy: 'engaged' },
      { key: 'a-l2-hi-3', fallback: 'That change reduces friction significantly.', energy: 'engaged' },
    ],
    medium: [
      { key: 'a-l2-md-1', fallback: 'A thoughtful refinement.', energy: 'calm' },
      { key: 'a-l2-md-2', fallback: 'That reads much more clearly now.', energy: 'calm' },
      { key: 'a-l2-md-3', fallback: 'Subtle but effective change.', energy: 'calm' },
    ],
    low: [
      { key: 'a-l2-lo-1', fallback: 'Attention to detail matters.', energy: 'calm' },
      { key: 'a-l2-lo-2', fallback: 'Small adjustments, big impact over time.', energy: 'calm' },
      { key: 'a-l2-lo-3', fallback: 'Good eye for nuance.', energy: 'calm' },
    ],
  },
  l3: {
    critical: [
      { key: 'a-l3-cr-1', fallback: 'Primary conflict pattern addressed.', energy: 'engaged' },
      { key: 'a-l3-cr-2', fallback: 'Highest-impact revision applied.', energy: 'engaged' },
      { key: 'a-l3-cr-3', fallback: 'Core escalation vector neutralized.', energy: 'excited' },
    ],
    high: [
      { key: 'a-l3-hi-1', fallback: 'Significant tone improvement applied.', energy: 'engaged' },
      { key: 'a-l3-hi-2', fallback: 'High-priority adjustment confirmed.', energy: 'calm' },
      { key: 'a-l3-hi-3', fallback: 'That revision shifts the dynamic meaningfully.', energy: 'engaged' },
    ],
    medium: [
      { key: 'a-l3-md-1', fallback: 'Revision applied. Score updated.', energy: 'calm' },
      { key: 'a-l3-md-2', fallback: 'Moderate improvement registered.', energy: 'calm' },
      { key: 'a-l3-md-3', fallback: 'Adjustment noted.', energy: 'calm' },
    ],
    low: [
      { key: 'a-l3-lo-1', fallback: 'Minor refinement recorded.', energy: 'calm' },
      { key: 'a-l3-lo-2', fallback: 'Detail-level edit applied.', energy: 'calm' },
      { key: 'a-l3-lo-3', fallback: 'Incremental improvement.', energy: 'calm' },
    ],
  },
};

// ---------------------------------------------------------------------------
// Crossing reactions — triggered when score crosses a category threshold
// ---------------------------------------------------------------------------

type CrossingBank = Record<VoiceTier, Record<CrossingTarget, Reaction[]>>;

export const CROSSING_REACTIONS: CrossingBank = {
  l1: {
    safe: [
      { key: 'x-l1-sf-1', fallback: 'We did it! Welcome to the chill zone.', energy: 'peak' },
      { key: 'x-l1-sf-2', fallback: 'Green means go... peacefully!', energy: 'excited' },
    ],
    caution: [
      { key: 'x-l1-ct-1', fallback: 'Getting calmer! Almost there.', energy: 'excited' },
      { key: 'x-l1-ct-2', fallback: 'The drama is fading. Keep going!', energy: 'engaged' },
    ],
    high_drama: [
      { key: 'x-l1-hd-1', fallback: 'Breaking out of the danger zone!', energy: 'excited' },
      { key: 'x-l1-hd-2', fallback: 'Progress! Still spicy, but improving.', energy: 'engaged' },
    ],
  },
  l2: {
    safe: [
      { key: 'x-l2-sf-1', fallback: 'Crossed into safe territory. Well done.', energy: 'excited' },
      { key: 'x-l2-sf-2', fallback: 'The message now reads constructively.', energy: 'engaged' },
    ],
    caution: [
      { key: 'x-l2-ct-1', fallback: 'Moved to caution range. Good progress.', energy: 'engaged' },
      { key: 'x-l2-ct-2', fallback: 'Tone is shifting noticeably. Keep refining.', energy: 'engaged' },
    ],
    high_drama: [
      { key: 'x-l2-hd-1', fallback: 'Down from extreme. Still room to improve.', energy: 'engaged' },
      { key: 'x-l2-hd-2', fallback: 'Making headway on the intensity.', energy: 'calm' },
    ],
  },
  l3: {
    safe: [
      { key: 'x-l3-sf-1', fallback: 'Score now in safe range. Objective met.', energy: 'engaged' },
      { key: 'x-l3-sf-2', fallback: 'Communication risk eliminated.', energy: 'engaged' },
    ],
    caution: [
      { key: 'x-l3-ct-1', fallback: 'Transitioned to caution tier. Improving.', energy: 'calm' },
      { key: 'x-l3-ct-2', fallback: 'Risk level reduced to moderate.', energy: 'calm' },
    ],
    high_drama: [
      { key: 'x-l3-hd-1', fallback: 'Exited extreme range. Continue adjustments.', energy: 'calm' },
      { key: 'x-l3-hd-2', fallback: 'Severity downgraded. Further revision advised.', energy: 'calm' },
    ],
  },
};

// ---------------------------------------------------------------------------
// Loading reactions — shown during rescore calculation
// ---------------------------------------------------------------------------

type LoadingBank = Record<VoiceTier, Reaction[]>;

export const LOADING_REACTIONS: LoadingBank = {
  l1: [
    { key: 'ld-l1-1', fallback: 'Recalculating the drama...', energy: 'calm' },
    { key: 'ld-l1-2', fallback: 'Running the numbers again...', energy: 'calm' },
    { key: 'ld-l1-3', fallback: 'Let me check that score...', energy: 'calm' },
    { key: 'ld-l1-4', fallback: 'Updating the drama meter...', energy: 'calm' },
  ],
  l2: [
    { key: 'ld-l2-1', fallback: 'Recalculating impact...', energy: 'calm' },
    { key: 'ld-l2-2', fallback: 'Updating score analysis...', energy: 'calm' },
    { key: 'ld-l2-3', fallback: 'Measuring the change...', energy: 'calm' },
  ],
  l3: [
    { key: 'ld-l3-1', fallback: 'Rescoring...', energy: 'calm' },
    { key: 'ld-l3-2', fallback: 'Computing revised score...', energy: 'calm' },
    { key: 'ld-l3-3', fallback: 'Processing update...', energy: 'calm' },
  ],
};

// ---------------------------------------------------------------------------
// Rescore result reactions — shown after server rescore completes successfully
// Supports {newScore} template var
// ---------------------------------------------------------------------------

type RescoreResultBank = Record<VoiceTier, Reaction[]>;

export const RESCORE_RESULT_REACTIONS: RescoreResultBank = {
  l1: [
    { key: 'rr-l1-1', fallback: 'The rewrite worked! Score confirmed at {newScore}.', energy: 'excited' },
    { key: 'rr-l1-2', fallback: "Numbers don't lie — your edit landed.", energy: 'engaged' },
    { key: 'rr-l1-3', fallback: 'Verdict is in! New score: {newScore}.', energy: 'excited' },
  ],
  l2: [
    { key: 'rr-l2-1', fallback: 'Confirmed: score updated to {newScore}.', energy: 'engaged' },
    { key: 'rr-l2-2', fallback: 'Rescore complete. Improvement verified.', energy: 'engaged' },
    { key: 'rr-l2-3', fallback: 'Your changes moved the score to {newScore}.', energy: 'engaged' },
  ],
  l3: [
    { key: 'rr-l3-1', fallback: 'Revised score: {newScore}. Analysis updated.', energy: 'calm' },
    { key: 'rr-l3-2', fallback: 'Score recalculation complete.', energy: 'calm' },
    { key: 'rr-l3-3', fallback: 'Server-verified score: {newScore}.', energy: 'calm' },
  ],
};

// ---------------------------------------------------------------------------
// Rescore failure reactions — shown when rescore fails gracefully
// ---------------------------------------------------------------------------

type RescoreFailureBank = Record<VoiceTier, Reaction[]>;

export const RESCORE_FAILURE_REACTIONS: RescoreFailureBank = {
  l1: [
    { key: 'rf-l1-1', fallback: 'Looks like the numbers are being shy. Using our best estimate.', energy: 'calm' },
    { key: 'rf-l1-2', fallback: 'The drama calculator needs a moment. Estimate shown for now.', energy: 'calm' },
  ],
  l2: [
    { key: 'rf-l2-1', fallback: 'Rescore unavailable. Using estimated score.', energy: 'calm' },
    { key: 'rf-l2-2', fallback: 'Server verification pending. Showing estimated impact.', energy: 'calm' },
  ],
  l3: [
    { key: 'rf-l3-1', fallback: 'Analysis update pending. Estimated score displayed.', energy: 'calm' },
    { key: 'rf-l3-2', fallback: 'Rescore service unavailable. Client estimate active.', energy: 'calm' },
  ],
};

// ---------------------------------------------------------------------------
// Complete reactions — shown when session is done (supports template vars)
// Template variables: {original}, {revised}, {delta}, {acceptedCount}
// ---------------------------------------------------------------------------

type CompleteBank = Record<VoiceTier, Reaction[]>;

export const COMPLETE_REACTIONS: CompleteBank = {
  l1: [
    { key: 'cp-l1-1', fallback: 'From {original} to {revised} — that is a full season redemption arc in one edit session.', energy: 'peak' },
    { key: 'cp-l1-2', fallback: 'And that is a wrap! {delta} points of drama, gone. The sequel looks much calmer.', energy: 'peak' },
    { key: 'cp-l1-3', fallback: '{acceptedCount} edits, {delta} points down. Your message just got a character glow-up.', energy: 'peak' },
  ],
  l2: [
    { key: 'cp-l2-1', fallback: '{delta}-point improvement across {acceptedCount} changes. Strong revision session.', energy: 'peak' },
    { key: 'cp-l2-2', fallback: 'Score moved from {original} to {revised}. All key suggestions applied.', energy: 'peak' },
  ],
  l3: [
    { key: 'cp-l3-1', fallback: 'Revision complete: {original} to {revised} ({delta}-point improvement). {acceptedCount} indicators addressed.', energy: 'peak' },
    { key: 'cp-l3-2', fallback: 'Session summary: {delta}-point reduction in drama indicators.', energy: 'peak' },
  ],
};
